"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = getOrderSummary;
var _functions = require("../utils/functions");
var _appConfig = require("../config/app-config");
/* eslint-disable complexity */
/* eslint-disable max-statements */

const SELECTION_DISALLOWED = ['none', 'disabled'];
class OrderSummaryFormatter {
  constructor({
    data,
    content,
    instance,
    environment
  }) {
    this.data = data;
    this.content = content;
    this.instance = instance;
    this.environment = environment;
    this.market = environment.market || _appConfig.DEFAULT_VALUES.market;
    this.currency = environment.currency || _appConfig.DEFAULT_VALUES.currency;
  }
  isAddonSites(selection) {
    return selection === 'addonSites';
  }

  /**
   * Validates if a selection is valid by checking if it exists and is not in the disallowed list
   * @param {string} selection - The selection value to validate, it can be 'none', 'disabled', or a valid selection (productName)
   * @returns {boolean} Returns true if the selection is valid (exists and not in SELECTION_DISALLOWED list), false otherwise
   */
  isValidSelection(selection) {
    return selection && !SELECTION_DISALLOWED.includes(selection);
  }
  createTermDisplayMap(termType, termLength) {
    const {
      oneTimeFee,
      monthPlural,
      monthSingular,
      perMonth,
      yearPlural,
      yearSingular,
      perYear
    } = this.content;
    const termDisplayMap = {
      [_appConfig.TERM_TYPES.UNKNOWN]: {
        fullWord: oneTimeFee,
        perAbbreviation: ''
      },
      [_appConfig.TERM_TYPES.MONTH]: {
        fullWord: termLength > 1 ? monthPlural : monthSingular,
        perAbbreviation: perMonth
      },
      [_appConfig.TERM_TYPES.YEAR]: {
        fullWord: termLength > 1 ? yearPlural : yearSingular,
        perAbbreviation: perYear
      }
    };
    return termDisplayMap[termType] || termDisplayMap[_appConfig.TERM_TYPES.UNKNOWN];
  }
  getConditionalTermLength(config) {
    const {
      attributes,
      displayableTerm,
      termLength,
      quantity,
      sale
    } = config;
    const {
      isFree,
      termDetails,
      fixedNumberOfTerms,
      display,
      resourceId,
      default: selection
    } = attributes;
    const {
      perMonth,
      users,
      sites,
      site
    } = this.content;
    let conditionalTermLength = `${termLength} ${displayableTerm.fullWord}`;
    if (resourceId || display === 'toggle') {
      if (quantity > 1) {
        const termPrice = (0, _functions.formatMicrounits)(sale, this.market, this.currency);
        conditionalTermLength = `${conditionalTermLength} x ${termPrice}${perMonth} x ${quantity} ${users}`;
      }
    } else if (isFree && !fixedNumberOfTerms) {
      conditionalTermLength = termDetails;
    } else if (this.isAddonSites(selection)) {
      const termPrice = (0, _functions.formatMicrounits)(sale, this.market, this.currency);
      const quantityText = quantity > 1 ? sites : site;
      conditionalTermLength = `${termLength} ${displayableTerm.fullWord} x ${termPrice}${perMonth} x ${quantity} ${quantityText}`;
    }
    return conditionalTermLength;
  }
  getConditionalTermLengthForRenewal(config) {
    const {
      priceDescription,
      isFree,
      renewalPrice,
      termLength
    } = config;
    const {
      renewsAt
    } = this.content;

    // If product is free, use priceDescription as-is (already formatted by API)
    if (isFree) {
      return priceDescription || null;
    }

    // For non-free products, always use renewsAt template
    return (0, _functions.getRenewalDetails)(renewalPrice, termLength, this.market, this.currency, renewsAt);
  }
  getUnitPercentage(termPrices, isFree, summaryTagLabel) {
    const {
      percentagesavingsformatted,
      percentagesavings
    } = termPrices || {};
    if (isFree) return summaryTagLabel;
    return percentagesavings ? this.content.savePercentage.replace('{(value)}', percentagesavings).replace('{value}', percentagesavings) : percentagesavingsformatted;
  }
  calculatePrices(config, isDomain) {
    const {
      sale,
      list,
      renewal,
      savings,
      termLength,
      quantity
    } = config;
    const finalTerm = isDomain ? 1 : termLength;
    return {
      price: sale * finalTerm * quantity,
      listPrice: list * finalTerm * quantity,
      renewalPrice: renewal * finalTerm * quantity,
      savingsAmount: savings * finalTerm * quantity
    };
  }
  processGroup({
    name,
    productType,
    displayArea,
    selection
  }, product) {
    const productData = this.data[product];
    const {
      properties
    } = this.instance[product];
    const {
      term: {
        termType,
        numberOfTerms
      }
    } = properties;
    let quantity = properties.quantity;
    const isPackage = productType === 'package';
    const isDomain = productType === 'domain';
    let attributeData, selectionData;
    if (isPackage || isDomain) {
      attributeData = productData;
      selectionData = productData.content;
    } else {
      // For top level products, we need to check if the product is in the displayArea
      attributeData = productData[displayArea]?.[product] ?? productData[displayArea]?.[name];
      selectionData = attributeData?.content?.[selection];
    }
    if (this.isAddonSites(selection)) {
      const customData = this.instance[product]?.[displayArea]?.[name]?.customData;
      quantity = customData ? parseInt(customData, 10) : 0;
      if (quantity === 0) return {};
    }
    if (!selectionData || !attributeData) return {};
    const {
      productName,
      description,
      image,
      priceDescription
    } = selectionData;
    const {
      prices,
      attributes
    } = attributeData;
    const {
      isFree,
      summaryTagLabel,
      hiddenInOrderSummary,
      fixedNumberOfTerms
    } = attributes;
    if (hiddenInOrderSummary) return {};
    const termLength = this.isAddonSites(selection) ? numberOfTerms : fixedNumberOfTerms || numberOfTerms;
    const unitPrices = prices?.[termLength] ?? prices?.[selection];
    const {
      list = 0,
      renewal = 0,
      sale = 0,
      savings = 0
    } = unitPrices || {};
    const finalSale = isFree ? 0 : sale;
    const finalSavings = isFree ? 0 : savings;
    const unitPercentage = this.getUnitPercentage(unitPrices, isFree, summaryTagLabel);
    const {
      price,
      listPrice,
      renewalPrice,
      savingsAmount
    } = this.calculatePrices({
      sale: finalSale,
      list,
      renewal,
      savings: finalSavings,
      termLength,
      quantity
    }, isDomain);
    const isOneTimeFee = termType === _appConfig.TERM_TYPES.UNKNOWN;
    // ONLY FOR DOMAINS: Convert term length to years
    const finalNumberOfTerms = isDomain ? termLength / 12 : termLength;
    const finalTermType = isDomain ? _appConfig.TERM_TYPES.YEAR : termType;
    const displayableTerm = this.createTermDisplayMap(finalTermType, finalNumberOfTerms);
    const termLengthConfig = {
      attributes,
      displayableTerm,
      termLength: finalNumberOfTerms,
      quantity,
      sale: finalSale
    };
    const conditionalTermLength = this.getConditionalTermLength(termLengthConfig);
    const finalRenewalPrice = this.isAddonSites(selection) ? price : renewalPrice;
    const renewalConfig = {
      renewal,
      displayableTerm,
      priceDescription,
      isFree,
      renewalPrice: finalRenewalPrice,
      termLength,
      price,
      selection
    };
    const conditionalTermLengthForRenewal = this.getConditionalTermLengthForRenewal(renewalConfig);
    const lineItemId = isPackage || isDomain ? product : selection;
    const instancePath = isPackage || isDomain ? product : `${product}/${displayArea}/${name}/${selection}`;
    return {
      selectionItem: {
        id: lineItemId,
        instancePath,
        productName,
        productLength: conditionalTermLength,
        todayPrice: (0, _functions.formatMicrounits)(price, this.market, this.currency),
        oldPrice: price !== listPrice ? (0, _functions.formatMicrounits)(listPrice, this.market, this.currency) : null,
        tag: unitPercentage,
        renewalPrice: conditionalTermLengthForRenewal,
        image,
        subTitle: description,
        unitPrice: finalSale,
        price,
        term: termLength,
        qty: quantity,
        isOneTimeFee,
        productType
      },
      internalPrice: price,
      internalSavings: savingsAmount
    };
  }
  sortProductEntries(productGroup) {
    // Priority map defines the order in which products should appear
    // Lower numbers = higher priority (appear first)
    const priorityMap = {
      properties: 1,
      // Main product always first
      dop: 2,
      // Domain protection (DOP) always after main product
      addonSites: 3,
      // Addon sites always after main product
      default: 999 // Default product always last
    };
    const getPriority = (name, item) => {
      if (name === 'properties') return priorityMap.properties;
      if (item.productType === 'domainprotection') return priorityMap.dop;
      if (this.isAddonSites(item.selection)) return priorityMap.addonSites;
      return priorityMap.default; // Everything else goes last with equal priority
    };

    // Sort entries by priority (ascending order)
    return Object.entries(productGroup).sort(([nameA, itemA], [nameB, itemB]) => {
      return getPriority(nameA, itemA) - getPriority(nameB, itemB);
    });
  }
  shouldShowInBundleMode(product) {
    const activeBundleId = (0, _functions.getActiveBundleId)(this.instance);

    // If bundle is not active, show everything
    if (!activeBundleId) {
      return true;
    }

    // If this is the bundle itself, show it
    if (product === activeBundleId) {
      return true;
    }
    return false;
  }
  processProduct(product) {
    const {
      properties,
      offers,
      includes,
      addons,
      banners
    } = this.instance[product];
    const productGroup = {
      properties,
      ...offers,
      ...includes,
      ...addons,
      ...banners
    };
    const selections = [];
    let totalPrice = 0;
    let totalSavings = 0;
    const sortedEntries = this.sortProductEntries(productGroup);
    sortedEntries.forEach(([name, {
      productType,
      displayArea,
      selection
    }]) => {
      // Check if this item should be shown based on bundle mode
      if (!this.shouldShowInBundleMode(product)) {
        return;
      }
      if (!this.isValidSelection(selection)) {
        return;
      }
      const groupConfig = {
        name,
        productType,
        displayArea,
        selection
      };
      const {
        selectionItem,
        internalPrice,
        internalSavings
      } = this.processGroup(groupConfig, product);
      if (selectionItem) {
        totalPrice += internalPrice;
        totalSavings += internalSavings;
        selections.push(selectionItem);
      }
    });
    return {
      selections,
      totalPrice: (0, _functions.formatMicrounits)(totalPrice, this.market, this.currency),
      totalSavings: totalSavings > 0 ? (0, _functions.formatMicrounits)(totalSavings, this.market, this.currency) : null,
      rawTotalPrice: totalPrice,
      rawTotalSavings: totalSavings
    };
  }
  generate() {
    const orderSummary = {};
    let totalPriceOrder = 0;
    let totalSavingsOrder = 0;
    Object.keys(this.instance).forEach(product => {
      const productResult = this.processProduct(product);
      const {
        rawTotalPrice,
        rawTotalSavings,
        ...productData
      } = productResult;

      // Store the product data without the raw values (for cleaner API)
      orderSummary[product] = productData;

      // Accumulate order totals from raw values
      totalPriceOrder += rawTotalPrice;
      totalSavingsOrder += rawTotalSavings;
    });
    orderSummary.totalPriceOrder = (0, _functions.formatMicrounits)(totalPriceOrder, this.market, this.currency);
    orderSummary.totalSavingsOrder = totalSavingsOrder > 0 ? (0, _functions.formatMicrounits)(totalSavingsOrder, this.market, this.currency) : null;
    return orderSummary;
  }
}
function getOrderSummary(responseData) {
  const formatter = new OrderSummaryFormatter(responseData);
  return formatter.generate();
}